/**
 * Sources / Default / Components / Custom > Form > Multi Select
 *
 * @copyright 2025 i-fabrik GmbH
 * @author Tobias Garz
 *
 * code by js library https://github.com/wenzhixin/multiple-select
 */

import {extend} from '../../../../../shared/utils';
import {
	isElement,
	isString
} from '../../../../../shared/utils/is';

import SelectorEngine from '../../../../../shared/dom/selector-engine';
import Data           from '../../../../../shared/dom/data';
import Manipulator    from '../../../../../shared/dom/manipulator';

import {multipleSelect} from '../../../../../../node_modules/multiple-select-vanilla/dist/browser/multiple-select';
import EventHandler from '../../../../../shared/dom/event-handler';

import {German, English} from '../../../../../../node_modules/multiple-select-vanilla/dist/locales/multiple-select-all-locales';

// -------
// Private
// -------

const NAME     = 'multi-select';
const DATA_KEY = `ifab.${NAME}`;
// const EVENT_KEY  = `.${DATA_KEY}`;
// const API_KEY    = '.data-api';

const DEFAULTS = {
	locale : German
};

class MultiSelect {
	constructor(element, o) {
		if(!element || !isElement(element)) {
			console.log('No valid element was given.');

			return;
		}

		if(Data.get(element, 'is-initialized')) {
			return;
		}

		this._api         = null;
		this._element     = element;
		this.tagContainer = this._getTagContainer();
		this.options      = extend(DEFAULTS, o, {
			onAfterCreate: () => {
				if(this.tagContainer) {
					let selectedOptions = SelectorEngine.find('option[selected="selected"]', this._element);

					if(selectedOptions.length) {
						for(let option of selectedOptions) {
							this.createTag({
								label   : option.innerHTML,
								selected: true,
								value   : option.value
							});
						}
					}
				}
			},
			onClick: (option) => {
				if(this.tagContainer) {
					if(option.selected) {
						this.createTag(option);
					} else {
						let tag = SelectorEngine.findOne(`[data-value="${option.value}"]`, this.tagContainer);
						tag.remove();
					}
				}
			}
		});

		this._isPending(true);

		this._api = multipleSelect(this._element, this.options);

		this._isPending(false);
		Data.set(element, 'is-initialized', true);
	}

	_isPending(state) {
		if(state) {
			Manipulator.addClass(this._element, 'is-pending');
			Data.set(this._element, 'is-pending', true);
		} else {
			Manipulator.removeClass(this._element, 'is-pending');
			Data.set(this._element, 'is-pending', false);
		}
	}

	_getTagContainer() {
		let containerId = Manipulator.getDataAttribute(this._element, 'select-tags');
		return containerId ? SelectorEngine.findOne(`#${containerId}`) : false;
	}

	createTag(option) {
		let tagTpl   = Manipulator.createElementFrom(`<span data-value="${option.value}" class="badge -grey-dark">${option.label}</span>`);
		let closeTpl = Manipulator.createElementFrom(`<span class="badge-close"><i class="fa-regular fa-times"></i></span>`);

		Manipulator.elementAppend(closeTpl, tagTpl);
		Manipulator.elementAppend(tagTpl, this.tagContainer);

		EventHandler.on(closeTpl, 'click', () => {
			this.removeTag(option, tagTpl);
		});
	}

	removeTag(option, tag) {
		tag.remove();
		this._api.uncheck(option.value);
	}
}

// -------
// Public
// -------

const init = () => {
	const COLLECTION = SelectorEngine.find(`.form-select[data-bind="${NAME}"]`);

	if(!COLLECTION.length) {
		return;
	}

	for(const ELEMENT of COLLECTION) {
		let _api = new MultiSelect(ELEMENT);
	}
};

// -------
// Export
// -------

export default {
	init: init
};

export {MultiSelect};